// Cart and checkout for the shop: the saved cart, product options, and the hook that owns every purchase step.
// Ported from the old storefront; endpoints, request bodies, messages and analytics are unchanged.
import { createElement, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { isVideo, shopApi, storeImageSrcset, storeImageUrl, track } from './api.js';

export const CART_KEY = '3dlabs.storeCart.v1';
const OPEN_EVENT = 'shop-cart-open';
const CLEAR_EVENT = 'shop-cart-clear';

function loadCart() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => Number(item?.product_id) && Number(item?.quantity) > 0).slice(0, 25) : [];
  } catch { return []; }
}

// For pages that do not hold the cart hook (the order page): open the drawer, or empty the cart after a payment.
export const openCart = () => window.dispatchEvent(new Event(OPEN_EVENT));
export function clearStoredCart() {
  try { localStorage.setItem(CART_KEY, '[]'); } catch { /* private mode */ }
  window.dispatchEvent(new Event(CLEAR_EVENT));
}

// Colour choices a customer may pick, links to the same part made for another model, and optional add-ons (another
// product that goes with this one, such as the tombstone with the console).
export function normalizeOptions(product) {
  const options = (Array.isArray(product?.options) ? product.options : []).filter(Boolean).map((option) => ({
    ...option,
    type: option.type === 'model' ? 'model' : option.type === 'addon' ? 'addon' : 'color',
    label: String(option.label || '').trim(),
    image_url: String(option.image_url || '').trim(),
    model_url: String(option.model_url || '').trim(),
    product_slug: String(option.product_slug || '').trim(),
  }));
  return {
    colors: options.filter((option) => option.type === 'color' && option.label && !option.hidden && option.available !== false),
    models: options.filter((option) => option.type === 'model' && option.label && option.product_slug),
    addons: options.filter((option) => option.type === 'addon' && option.label && !option.hidden),
  };
}

// The colour picked on a product page. Every buy box for that product shares it (the main one and the sticky bar),
// and it is forgotten once the last of them leaves the page, so coming back starts from the first colour again.
const chosen = new Map();
const watchers = new Set();
const announce = () => watchers.forEach((watcher) => watcher());
const watch = (watcher) => { watchers.add(watcher); return () => watchers.delete(watcher); };
export function useColorChoice(slug) {
  const id = useSyncExternalStore(watch, () => chosen.get(slug)?.id || '');
  useEffect(() => {
    const entry = chosen.get(slug) || { id: '', users: 0 };
    entry.users += 1;
    chosen.set(slug, entry);
    return () => { entry.users -= 1; if (entry.users <= 0 && chosen.get(slug) === entry) { chosen.delete(slug); announce(); } };
  }, [slug]);
  const choose = (next) => {
    const entry = chosen.get(slug) || { id: '', users: 0 };
    entry.id = next;
    chosen.set(slug, entry);
    announce();
  };
  return [id, choose];
}

// The choice in force on a product page (the first one until the customer picks). A choice can be a colour or a
// version of the product; a version may bring its own 3D model (`model_url`) and photo, which the page then shows.
export function useChosenOption(product) {
  const colors = useMemo(() => normalizeOptions(product).colors, [product]);
  const [id] = useColorChoice(product?.slug);
  return colors.find((option) => option.id === id) || colors[0] || null;
}

// A small product picture. Store photos load resized; if a resized copy cannot be made it falls back to the file as
// uploaded, and it shows nothing at all when there is no picture or it cannot be loaded.
export function Photo({ src, alt = '', sizes = '96px', width = 240, className }) {
  const url = String(src || '').trim();
  const [original, setOriginal] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setOriginal(false); setFailed(false); }, [url]);
  if (!url || failed) return null;
  if (isVideo(url)) return createElement('video', { className, src: url, autoPlay: true, loop: true, muted: true, playsInline: true, preload: 'metadata', disablePictureInPicture: true, onError: () => setFailed(true) });
  const resized = original ? null : storeImageSrcset(url);
  const shared = { className, alt, loading: 'lazy', decoding: 'async' };
  if (resized) return createElement('img', { ...shared, src: storeImageUrl(url, width), srcSet: resized, sizes, onError: () => setOriginal(true) });
  return createElement('img', { ...shared, src: url, onError: () => setFailed(true) });
}

const FULFILLMENT_KEY = '3dlabs.storeFulfillment';

// Pickup phone (the host checks the same rules): a 10-digit North American number, shown as (905) 555-0123.
const phoneDigits = (value) => { const d = String(value || '').replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d; };
export function phoneProblem(value) {
  const text = String(value || '').trim(), d = phoneDigits(text);
  if (!text) return 'Enter a phone number so we can text or call you to arrange pickup.';
  if (/[^\d\s().+-]/.test(text)) return 'Enter just a phone number, like (905) 555-0123.';
  if (d.length !== 10) return 'Enter a 10-digit phone number, like (905) 555-0123.';
  if (!/^[2-9]/.test(d) || /^[2-9]11/.test(d)) return 'That area code does not look right. Check the first 3 digits.';
  if (!/^[2-9]/.test(d.slice(3)) || /^[2-9]11/.test(d.slice(3))) return 'That phone number does not look right. Check the digits after the area code.';
  return '';
}
export const formatPhone = (value) => { const d = phoneDigits(value); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(value || '').trim(); };
// Where a shipped order goes, for its shipping price (the street is asked by Square at payment).
const SHIP_TO_KEY = '3dlabs.storeShipTo';
const POSTAL_PROVINCE = { A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC', K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK', T: 'AB', V: 'BC', Y: 'YT' };
export function shipToReady(to) {
  const postal = String(to?.postal_code || '').toUpperCase().replace(/\s+/g, '');
  if (to?.country === 'CA') return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/.test(postal) && Boolean(to.province || POSTAL_PROVINCE[postal[0]]);
  if (to?.country === 'US') return /^\d{5}(-?\d{4})?$/.test(postal) && /^[A-Z]{2}$/.test(String(to.province || ''));
  return false;
}
export const provinceFromPostal = (postal) => POSTAL_PROVINCE[String(postal || '').trim().toUpperCase()[0]] || '';
const cartSignature = (lines) => lines.map((item) => `${item.product_id}:${item.option_id || ''}x${item.quantity}`).sort().join(',');
const sameLine = (a, b) => a.product_id === b.product_id && a.option_id === b.option_id;
const lineKey = (item) => `${item.product_id}:${item.option_id}`;

// Owns the cart and both ways to pay. `payment` (the shop's payment settings) is accepted for the shell's
// convenience; the old shop never consulted it before starting a checkout, so this does not either.
export function useCart({ payment } = {}) {
  const [items, setItems] = useState(loadCart);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  // Ship it or pick it up. The choice is remembered, and only counts while the shop offers pickup.
  const [chosen, setChosen] = useState(() => { try { return localStorage.getItem(FULFILLMENT_KEY) === 'pickup' ? 'pickup' : 'ship'; } catch { return 'ship'; } });
  const pickupAvailable = Boolean(payment?.pickup_available);
  const fulfillment = pickupAvailable && chosen === 'pickup' ? 'pickup' : 'ship';
  const setFulfillment = (next) => { const value = next === 'pickup' ? 'pickup' : 'ship'; setChosen(value); try { localStorage.setItem(FULFILLMENT_KEY, value); } catch { /* private mode */ } };
  // A pickup order can be paid in cash on collection when the shop offers it. No online checkout runs then, so the
  // name and email it would have collected are asked for in the shop.
  const [payBy, setPayBy] = useState('card');
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const cashAvailable = pickupAvailable && Boolean(payment?.pickup_cash_available);
  const cash = fulfillment === 'pickup' && cashAvailable && payBy === 'cash';
  const cashReady = () => {
    // Every pickup order (online or cash) needs a phone so we can text or call to arrange it. The host checks it too.
    if (fulfillment === 'pickup') {
      const problem = phoneProblem(customer.phone);
      if (problem) { setMessage(problem); return false; }
    }
    if (!cash || (customer.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim()))) return true;
    setMessage('Enter your name and email so we can confirm your pickup order.');
    return false;
  };
  // Shipping (paid by the customer): the place it goes, the prices quoted for this exact cart, and the one chosen.
  const [shipTo, setShipToState] = useState(() => { try { return { country: 'CA', postal_code: '', province: '', ...JSON.parse(localStorage.getItem(SHIP_TO_KEY) || '{}') }; } catch { return { country: 'CA', postal_code: '', province: '' }; } });
  const [shipQuote, setShipQuote] = useState(null);
  const [shipChoice, setShipChoice] = useState('');
  const [shipBusy, setShipBusy] = useState(false);
  const [shipError, setShipError] = useState('');
  const setShipTo = (next) => { setShipToState(next); setShipQuote(null); setShipError(''); try { localStorage.setItem(SHIP_TO_KEY, JSON.stringify(next)); } catch { /* private mode */ } };
  const signature = cartSignature(items);
  // Shipping is charged at checkout once the shop switches it on (the host says so with the payment settings).
  const shippingOn = Boolean(payment?.shipping_at_checkout);
  const shipCountries = Array.isArray(payment?.ship_countries) && payment.ship_countries.length ? payment.ship_countries : ['CA'];
  const shipping = shippingOn && fulfillment === 'ship' && shipQuote?.signature === signature ? shipQuote : null;
  const shipOption = shipping?.options.find((option) => option.id === shipChoice) || null;
  // A remembered US address does not count while the shop ships within Canada only.
  const shipToNow = shipCountries.includes(shipTo.country) ? shipTo : { country: 'CA', postal_code: '', province: '' };
  const quoteShipping = async () => {
    if (!items.length || !shipToReady(shipToNow)) return;
    const asked = signature;
    setShipBusy(true); setShipError('');
    try {
      const to = { ...shipToNow, province: shipToNow.province || (shipToNow.country === 'CA' ? provinceFromPostal(shipToNow.postal_code) : '') };
      const result = await shopApi('/public/store/shipping/quote', { method: 'POST', body: { items: items.map((item) => ({ product_id: item.product_id, option_id: item.option_id, quantity: item.quantity })), address: to } });
      setShipQuote({ ...result, signature: asked });
      setShipChoice(result.options[0]?.id || '');
    } catch (error) { setShipQuote(null); setShipError(error.message || 'Shipping prices are not available right now.'); }
    finally { setShipBusy(false); }
  };
  const shipReady = () => {
    if (fulfillment !== 'ship' || !shippingOn) return true;
    if (shipOption) return true;
    setMessage('Enter where it is going to see the shipping price, then choose shipping.');
    return false;
  };
  const shipDetails = () => fulfillment === 'ship' && shipOption ? { shipping: { quote_id: shipping.quote_id, option_id: shipOption.id } } : {};
  const cashDetails = () => ({
    ...(fulfillment === 'pickup' ? { phone: formatPhone(customer.phone) } : {}),
    ...(cash ? { payment: 'cash', customer: { name: customer.name.trim(), email: customer.email.trim() } } : {}),
  });

  useEffect(() => { try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch { /* private mode */ } }, [items]);
  useEffect(() => {
    const show = () => setOpen(true), empty = () => setItems([]);
    window.addEventListener(OPEN_EVENT, show);
    window.addEventListener(CLEAR_EVENT, empty);
    return () => { window.removeEventListener(OPEN_EVENT, show); window.removeEventListener(CLEAR_EVENT, empty); };
  }, []);

  const count = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
  const totalCents = items.reduce((total, item) => total + Number(item.price_cents || 0) * Number(item.quantity || 0), 0);
  const keyOf = (item) => typeof item === 'string' ? item : item?.key;

  const add = (product, option = null) => {
    const optionId = String(option?.id || '');
    const key = `${product.id}:${optionId}`;
    setItems((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) return current.map((item) => item.key === key ? { ...item, quantity: Math.min(20, item.quantity + 1) } : item);
      return [...current, {
        key,
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        image: option?.image_url || product.image || '',
        option_id: optionId,
        option_label: option?.label || '',
        price_cents: product.price_cents,
        currency: product.currency || 'CAD',
        quantity: 1,
      }];
    });
    setMessage(`${product.name} added to cart.`);
    setOpen(true);
    track('store_cart_add', product.id, { source: 'product_page', option_id: optionId || null }, { product_slug: product.slug });
  };

  // A quantity of 0 removes the line, so stepping down from 1 takes the product out of the cart.
  const setQuantity = (item, quantity) => setItems((current) => current
    .map((line) => line.key === keyOf(item) ? { ...line, quantity: Math.max(0, Math.min(20, quantity)) } : line)
    .filter((line) => line.quantity > 0));
  const remove = (item) => setItems((current) => current.filter((line) => line.key !== keyOf(item)));
  const clear = () => setItems([]);

  // Checks the cart against today's prices first. If anything changed, the cart is updated and the customer is told;
  // no payment starts until they choose checkout again.
  const checkoutCart = async () => {
    if (!items.length || !cashReady() || !shipReady()) return;
    setBusy(true);
    setMessage('');
    try {
      const quote = await shopApi('/public/store/cart/quote', { method: 'POST', body: { items: items.map((item) => ({ product_id: item.product_id, option_id: item.option_id, quantity: item.quantity })) } });
      if (quote.items.some((item) => { const previous = items.find((line) => sameLine(line, item)); return !previous || previous.price_cents !== item.price_cents || previous.currency !== item.currency || (previous.option_label || '') !== item.option_label; })) {
        setItems(quote.items.map((item) => ({ ...items.find((line) => sameLine(line, item)), ...item, key: lineKey(item) })));
        setMessage('Your cart has changed. Review the updated price or finish, then choose checkout again. No payment was started.');
        return;
      }
      track('store_cart_checkout_click', null, { source: 'cart' }, { item_count: count, line_count: items.length, total_cents: totalCents });
      const result = await shopApi('/public/store/cart/checkout', {
        method: 'POST',
        body: { fulfillment, ...cashDetails(), ...shipDetails(), items: items.map((item) => ({ product_id: item.product_id, option_id: item.option_id, quantity: item.quantity, expected_price_cents: item.price_cents, expected_currency: item.currency, expected_option_label: item.option_label || '' })) },
      });
      if (result.placed && result.order_url) location.assign(result.order_url);
      else if (result.checkout_url) location.assign(result.checkout_url);
      else setMessage(result.message || 'Checkout is not available yet.');
    } catch (error) {
      if (error.details?.code === 'cart_changed') setItems(error.details.items.map((item) => ({ ...items.find((line) => sameLine(line, item)), ...item, key: lineKey(item) })));
      if (error.details?.code === 'shipping_quote') setShipQuote(null);
      setMessage(error.message || 'Checkout is not available yet.');
    } finally {
      setBusy(false);
    }
  };

  // Buys one of a single product straight away, without going through the cart.
  const buyNow = async (product, option = null) => {
    if (fulfillment === 'ship' && shippingOn) { add(product, option); setMessage('Enter where it is going to see the shipping price, then check out.'); return; }
    if (!cashReady()) return;
    setBusy(true);
    setMessage('');
    try {
      track('site_checkout_click', product.id, { source: 'checkout_button' }, { product_slug: product.slug, product_name: product.name, price_cents: product.price_cents, currency: product.currency });
      const result = await shopApi('/public/store/checkout', { method: 'POST', body: { fulfillment, ...cashDetails(), product_id: product.id, option_id: option?.id || '', quantity: 1, expected_price_cents: product.price_cents, expected_currency: product.currency, expected_option_label: option?.label || '' } });
      if (result.placed && result.order_url) location.assign(result.order_url);
      else if (result.checkout_url) location.assign(result.checkout_url);
      else if (result.pending) setMessage(result.message || 'Payment link pending. Contact 3DLabs to order.');
    } catch (error) {
      // The host found a different price or finish: put its version in the cart and show the cart.
      if (error.details?.code === 'cart_changed') {
        setOpen(true);
        setItems((current) => {
          const next = [...current];
          for (const item of error.details.items) {
            const key = lineKey(item), index = next.findIndex((line) => line.key === key), updated = { ...item, key, image: product.image || '' };
            if (index < 0) next.push(updated); else next[index] = updated;
          }
          return next;
        });
      }
      setMessage(error.message || 'Checkout is not configured yet.');
    } finally {
      setBusy(false);
    }
  };

  return { items, count, totalCents, currency: items[0]?.currency || 'CAD', open, setOpen, busy, message, clearMessage: () => setMessage(''), add, setQuantity, remove, clear, checkoutCart, buyNow, fulfillment, setFulfillment, pickupAvailable, pickupArea: String(payment?.pickup_area || ''), cashAvailable, cash, setPayBy: (next) => setPayBy(next === 'cash' ? 'cash' : 'card'), customer, setCustomer,
    shippingOn, shipCountries, shipTo: shipToNow, setShipTo, shipping, shipOption, chooseShipping: setShipChoice, quoteShipping, shipBusy, shipError, shipToReady: shipToReady(shipToNow) };
}
